import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, message, Tabs, Space } from 'antd';
import { 
  SettingOutlined, 
  SaveOutlined, 
  ApiOutlined, 
  SecurityScanOutlined,
  ExperimentOutlined 
} from '@ant-design/icons';

const { Option } = Select;

interface ApiType {
  api_type_id: string;
  api_key: string;
  api_name: string;
  description: string;
  display_order: number;
  test_api_url?: string;
}

// 带重试的 fetch 请求（网络丢包容错）
const fetchWithRetry = async (url: string, options: RequestInit = {}, maxRetries = 6): Promise<Response> => {
  let lastError: Error | null = null;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);
      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (attempt < maxRetries) {
        const retryDelay = attempt * 2000;
        console.warn(`🔄 fetch 重试 (${attempt}/${maxRetries}): ${url}, ${retryDelay}ms 后重试, 错误: ${lastError.message}`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }
  throw lastError || new Error('请求失败');
};

const SettingsPageNexus: React.FC = () => {
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [apiTypes, setApiTypes] = useState<ApiType[]>([]);
  const [selectedApiType, setSelectedApiType] = useState<string>('');
  const [apiKey, setApiKey] = useState<string>('');

  const fetchApiTypes = async () => {
    try {
      const response = await fetchWithRetry('/api/api-types', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      const result = await response.json();
      if (result.success) {
        setApiTypes(result.data);
        if (result.data && result.data.length > 0) {
          const firstApiType = result.data[0];
          setSelectedApiType(firstApiType.api_key);
          fetchApiKey(firstApiType.api_key);
        }
      } else {
        message.error(result.message || '获取API类型失败');
      }
    } catch (error) {
      console.error('获取API类型失败:', error);
      message.error('网络不稳定，获取API类型失败，请刷新重试');
    }
  };

  const fetchApiKey = async (apiKey: string) => {
    try {
      const response = await fetchWithRetry(`/api/api-config/${apiKey}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      const result = await response.json();
      if (result.success) {
        const configValue = result.data?.config_value || '';
        setApiKey(configValue);
      } else {
        setApiKey('');
      }
    } catch (error) {
      console.error('获取API密钥失败:', error);
      setApiKey('');
    }
  };

  const handleApiTypeChange = (value: string) => {
    setSelectedApiType(value);
    fetchApiKey(value);
  };

  const handleSave = async (values: any) => {
    try {
      setLoading(true);
      const response = await fetchWithRetry('/api/api-config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          config_key: selectedApiType,
          config_value: values.api_key
        })
      });
      const result = await response.json();
      if (result.success) {
        message.success('保存成功');
        setApiKey(values.api_key);
      } else {
        message.error(result.message || '保存失败');
      }
    } catch (error) {
      console.error('保存失败:', error);
      message.error('网络不稳定，保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 测试API连接
  const handleTestApi = async () => {
    if (!selectedApiType) {
      message.error('请选择API类型');
      return;
    }

    const currentApiType = apiTypes.find(type => type.api_key === selectedApiType);
    if (!currentApiType?.test_api_url) {
      message.warning('暂时不支持测试该API类型');
      return;
    }

    if (!apiKey.trim()) {
      message.error('请先配置API密钥');
      return;
    }

    setTestLoading(true);
    try {
      const response = await fetchWithRetry(`/api/test-api/${selectedApiType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      const result = await response.json();
      if (result.success) {
        message.success('API连接测试成功！');
      } else {
        message.error(result.message || 'API连接测试失败');
      }
    } catch (error) {
      console.error('测试API连接失败:', error);
      message.error('网络不稳定，测试API连接失败，请重试');
    } finally {
      setTestLoading(false);
    }
  };

  // 修改密码
  const handleChangePassword = async (values: any) => {
    const { currentPassword, newPassword, confirmPassword } = values;

    if (newPassword !== confirmPassword) {
      message.error('新密码和确认密码不一致');
      return;
    }

    setLoading(true);
    try {
      const response = await fetchWithRetry('/api/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        }),
      });

      const result = await response.json();
      if (result.success) {
        message.success('密码修改成功');
        passwordForm.resetFields();
      } else {
        message.error(result.message || '密码修改失败');
      }
    } catch (error) {
      console.error('修改密码失败:', error);
      message.error('网络不稳定，密码修改失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiTypes();
  }, []);

  useEffect(() => {
    form.setFieldsValue({ 
      api_key: apiKey,
      api_type: selectedApiType 
    });
  }, [apiKey, selectedApiType, form]);

  const tabItems = [
    {
      key: 'api',
      label: (
        <Space>
          <ApiOutlined />
          API设置
        </Space>
      ),
      children: (
        <div className="bg-white rounded-2xl p-8 border border-gray-200">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2 flex items-center">
              <ApiOutlined style={{ fontSize: '24px', marginRight: '8px', color: '#6366F1' }} />
              API配置
            </h2>
            <p className="text-gray-600">配置第三方API密钥</p>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={{ api_key: apiKey }}
          >
            <Form.Item
              label={<span className="text-gray-700 font-medium">API类型</span>}
              name="api_type"
            >
              <Select
                onChange={handleApiTypeChange}
                size="large"
                className="w-full"
                placeholder="请选择API类型"
              >
                {apiTypes.map(type => (
                  <Option key={type.api_key} value={type.api_key}>
                    {type.api_name}
                    {type.description && (
                      <span className="text-gray-400 ml-2">- {type.description}</span>
                    )}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={<span className="text-gray-700 font-medium">API密钥</span>}
              name="api_key"
              rules={[{ required: true, message: '请输入API密钥' }]}
            >
              <Input.Password
                placeholder="请输入API密钥"
                size="large"
                className="w-full"
              />
            </Form.Item>

            <Form.Item>
              <div className="flex justify-end space-x-3">
                <Button
                  onClick={handleTestApi}
                  loading={testLoading}
                  size="large"
                  icon={<ExperimentOutlined />}
                  style={{ 
                    borderRadius: '12px',
                    height: '48px',
                    padding: '0 32px'
                  }}
                >
                  测试API
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  icon={<SaveOutlined />}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 border-none hover:shadow-lg"
                  style={{ 
                    background: 'linear-gradient(to right, #4F46E5, #7C3AED)',
                    borderRadius: '12px',
                    height: '48px',
                    padding: '0 32px'
                  }}
                >
                  保存配置
                </Button>
              </div>
            </Form.Item>
          </Form>
        </div>
      )
    },
    {
      key: 'security',
      label: (
        <Space>
          <SecurityScanOutlined />
          安全设置
        </Space>
      ),
      children: (
        <div className="bg-white rounded-2xl p-8 border border-gray-200">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2 flex items-center">
              <SecurityScanOutlined style={{ fontSize: '24px', marginRight: '8px', color: '#6366F1' }} />
              修改密码
            </h2>
            <p className="text-gray-600">更新您的登录密码</p>
          </div>

          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handleChangePassword}
          >
            <Form.Item
              name="currentPassword"
              label={<span className="text-gray-700 font-medium">当前密码</span>}
              rules={[{ required: true, message: '请输入当前密码' }]}
            >
              <Input.Password 
                placeholder="请输入当前密码" 
                size="large"
              />
            </Form.Item>
            
            <Form.Item
              name="newPassword"
              label={<span className="text-gray-700 font-medium">新密码</span>}
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '密码长度至少6位' }
              ]}
            >
              <Input.Password 
                placeholder="请输入新密码" 
                size="large"
              />
            </Form.Item>
            
            <Form.Item
              name="confirmPassword"
              label={<span className="text-gray-700 font-medium">确认新密码</span>}
              rules={[
                { required: true, message: '请确认新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password 
                placeholder="请确认新密码" 
                size="large"
              />
            </Form.Item>
            
            <Form.Item>
              <div className="flex justify-end">
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  style={{ 
                    background: 'linear-gradient(to right, #4F46E5, #7C3AED)',
                    borderRadius: '12px',
                    height: '48px',
                    padding: '0 32px'
                  }}
                >
                  修改密码
                </Button>
              </div>
            </Form.Item>
          </Form>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center">
            <SettingOutlined style={{ fontSize: '36px', marginRight: '12px', color: '#6366F1' }} />
            设置
          </h1>
          <p className="text-lg text-gray-600">管理您的系统配置和API密钥</p>
        </div>

        <Tabs defaultActiveKey="api" size="large" items={tabItems} />
      </div>
    </div>
  );
};

export default SettingsPageNexus;
